export const VALIDATION_INTENTS = [
  "trigger",
  "evidence",
  "delivery",
  "commitment",
  "decision",
] as const;

export type ValidationIntent = (typeof VALIDATION_INTENTS)[number];
export type ValidationLocale = "en" | "pt";
export type LocalizedValidationText = Record<ValidationLocale, string>;

export interface ValidationPlaybookStep {
  intent: ValidationIntent;
  window: string;
  goal: string;
  actions: string[];
  artifact: string;
  metric: string;
  gate: string;
}

export type LocalizedValidationPlaybook = Record<ValidationLocale, ValidationPlaybookStep[]>;

export interface ValidationPlaybookSource {
  slug: string;
  verdict: "build" | "adapt" | "do_not_build_yet";
  title: LocalizedValidationText;
  targetCustomer: LocalizedValidationText;
  purchaseTrigger: LocalizedValidationText;
  initialOffer: LocalizedValidationText;
  distribution: LocalizedValidationText;
  businessModel: LocalizedValidationText;
  problem: LocalizedValidationText;
  wedge: LocalizedValidationText;
  coreLoop: LocalizedValidationText;
  smallestTest: LocalizedValidationText;
  killCriterion: LocalizedValidationText;
  risks: Record<ValidationLocale, string[]>;
}

function boundedInteger(value: number, fallback: number, minimum: number, maximum: number): number {
  if (!Number.isSafeInteger(value)) return fallback;
  return Math.min(maximum, Math.max(minimum, value));
}

function firstThreshold(value: string, patterns: RegExp[], fallback: number): number {
  for (const pattern of patterns) {
    const match = pattern.exec(value);
    if (match?.[1]) return boundedInteger(Number.parseInt(match[1], 10), fallback, 1, 100);
  }
  return fallback;
}

function thresholdsFor(source: ValidationPlaybookSource): {
  conversations: number;
  repeatedTriggers: number;
  cases: number;
  completedCases: number;
  commitments: number;
  offerAudience: number;
} {
  const test = source.smallestTest.en;
  const kill = source.killCriterion.en;
  const conversations = firstThreshold(`${test} ${kill}`, [
    /\b(\d+)\s+(?:qualified\s+)?(?:discovery\s+)?(?:calls?|conversations?|interviews?)\b/i,
    /\b(?:interview|complete|run)\s+(\d+)\s+(?:[a-z-]+\s+){0,2}(?:calls?|conversations?|interviews?|buyers?|operators?|leads?)\b/i,
  ], 5);
  const cases = firstThreshold(test, [
    /\b(\d+)\s+(?:bounded\s+|permitted\s+|current\s+|real\s+)?(?:cases?|workflows?|deliveries?|pilots?|drills?|audits?)\b/i,
  ], 3);
  const commitments = firstThreshold(`${test} ${kill}`, [
    /\b(?:at\s+least\s+)?(\d+)\s+(?:of\s+(?:up\s+to\s+)?\d+\s+)?(?:accountable\s+)?(?:buyers?|customers?|operators?|teams?)\b[^.]{0,90}\b(?:pay|pays|paid|payment|contract|deposit|budget|purchase order|funded)\b/i,
    /\b(?:at\s+least\s+)?(\d+)\s+(?:paid\s+)?(?:pilots?|drills?|audits?|contracts?|commitments?)\b/i,
  ], 2);
  return {
    conversations,
    repeatedTriggers: Math.min(conversations, Math.max(2, Math.ceil(conversations / 3))),
    cases,
    completedCases: Math.min(cases, Math.max(1, Math.min(2, cases))),
    commitments,
    offerAudience: Math.max(3, commitments),
  };
}

function EnglishPlaybook(source: ValidationPlaybookSource): ValidationPlaybookStep[] {
  const counts = thresholdsFor(source);
  const firstRisk = source.risks.en[0] ?? "The current evidence may not support the proposed opportunity.";
  const deliveryGoal = source.verdict === "build"
    ? "Prototype only the bounded decision loop"
    : source.verdict === "adapt"
      ? "Deliver the narrow wedge manually"
      : "Rehearse the wedge without building software";
  const deliveryAction = source.verdict === "build"
    ? `Build only the smallest reversible prototype needed to run this wedge: ${source.wedge.en}`
    : source.verdict === "adapt"
      ? `Run the wedge as a manual or concierge delivery before automating it: ${source.wedge.en}`
      : `Do not build software; replay the wedge with permitted, synthetic, or de-identified evidence: ${source.wedge.en}`;
  const commitmentGate = source.verdict === "do_not_build_yet"
    ? `Move to the final decision only if at least ${counts.commitments} of ${counts.offerAudience} accountable buyers provide the missing permitted evidence and budget authority or make a paid commitment; otherwise preserve do_not_build_yet.`
    : `Move to the final decision only if at least ${counts.commitments} of ${counts.offerAudience} accountable buyers make a paid commitment through a received payment, signed contract, deposit, purchase order, or budget approval.`;

  return [
    {
      intent: "trigger",
      window: "Days 1–2",
      goal: `Confirm the accountable trigger for ${source.title.en}`,
      actions: [
        `Assemble the prospect set through this documented route: ${source.distribution.en}`,
        `Interview the accountable buyer defined in the dossier: ${source.targetCustomer.en}`,
        `For every conversation, record whether this trigger is live, dated, and owned: ${source.purchaseTrigger.en}`,
      ],
      artifact: `A trigger ledger for ${source.title.en} with buyer, live event, deadline, current owner, source, and next evidence step.`,
      metric: `Complete ${counts.conversations} buyer conversations; count only an observable current workflow and trigger, never scheduled calls or stated interest.`,
      gate: `Continue only if at least ${counts.repeatedTriggers} of ${counts.conversations} accountable buyers document the same current workflow trigger and a real decision, exception, or deadline.`,
    },
    {
      intent: "evidence",
      window: "Days 3–5",
      goal: "Falsify the problem in current work",
      actions: [
        `Trace ${counts.cases} permitted current cases against this problem: ${source.problem.en}`,
        "For each case, log the current workaround, decision owner, exception, observed outcome, and one piece of disconfirming evidence.",
        `Test the highest-risk assumption before delivery: ${firstRisk}`,
      ],
      artifact: `A current-state evidence map for ${source.title.en} with ${counts.cases} source-linked cases, their workaround, owner, exception, counterevidence, and recorded result.`,
      metric: `Review ${counts.cases} current cases and preserve at least 1 counterexample instead of filtering the evidence toward a positive conclusion.`,
      gate: `Advance only if at least ${counts.completedCases} of ${counts.cases} cases show the repeated decision gap and an accountable buyer confirms the recorded workflow; otherwise stop or keep the current verdict.`,
    },
    {
      intent: "delivery",
      window: "Days 6–8",
      goal: deliveryGoal,
      actions: [
        deliveryAction,
        `Keep the delivery inside this offer boundary: ${source.initialOffer.en}`,
        `Record every input, handoff, exception, and decision while running this loop: ${source.coreLoop.en}`,
      ],
      artifact: `A bounded ${source.title.en} delivery packet containing inputs, source links, exceptions, human review, and the buyer's approve, reject, or defer decision.`,
      metric: `Run ${counts.cases} bounded cases and record the buyer-controlled decision for each case, including negative and inconclusive outcomes.`,
      gate: `Continue only if at least ${counts.completedCases} of ${counts.cases} cases reach a recorded approve, reject, defer, or cutover decision and the accountable buyer completes the handoff.`,
    },
    {
      intent: "commitment",
      window: "Days 9–11",
      goal: source.verdict === "do_not_build_yet" ? "Test whether the evidence earns commitment" : "Ask for a funded continuation",
      actions: [
        `Present a fixed-scope continuation only after the delivery gate; use this commercial hypothesis: ${source.businessModel.en}`,
        `Return to the accountable buyer through the documented route: ${source.distribution.en}`,
        "Record the exact price, scope, objection, contract or budget decision, and whether the buyer controls the next trigger.",
      ],
      artifact: `A fixed-scope offer and objections ledger for ${source.title.en}, linked to the delivered evidence and each buyer's budget decision.`,
      metric: `Present the offer to ${counts.offerAudience} accountable buyers; count only received payment, signed contract, deposit, purchase order, budget approval, or explicit rejection.`,
      gate: commitmentGate,
    },
    {
      intent: "decision",
      window: "Days 12–14",
      goal: "Apply the go, adapt, or stop rule",
      actions: [
        `Score the observed evidence against the dossier's smallest test without substituting softer metrics: ${source.smallestTest.en}`,
        `Apply this kill criterion without reinterpretation: ${source.killCriterion.en}`,
        "Publish one decision memo that marks every threshold met, missed, or unknown and names the next owner and deadline.",
      ],
      artifact: `A signed go, adapt, or stop memo for ${source.title.en}, with the evidence ledger, unresolved risks, smallest-test score, kill-rule result, owner, and next date.`,
      metric: "Produce 1 explicit decision by day 14; every threshold must be marked met, missed, or unknown.",
      gate: `Advance only when the smallest test is met and the kill rule is not triggered. Smallest test: ${source.smallestTest.en} Kill rule: ${source.killCriterion.en}`,
    },
  ];
}

function PortuguesePlaybook(source: ValidationPlaybookSource): ValidationPlaybookStep[] {
  const counts = thresholdsFor(source);
  const firstRisk = source.risks.pt[0] ?? "A evidência atual pode não sustentar a oportunidade proposta.";
  const deliveryGoal = source.verdict === "build"
    ? "Prototipar somente o loop de decisão limitado"
    : source.verdict === "adapt"
      ? "Executar manualmente a menor unidade de valor"
      : "Ensaiar a menor unidade de valor sem construir software";
  const deliveryAction = source.verdict === "build"
    ? `Construa apenas o menor protótipo reversível necessário para executar esta proposta: ${source.wedge.pt}`
    : source.verdict === "adapt"
      ? `Execute a menor unidade de valor como entrega manual ou concierge antes de automatizá-la: ${source.wedge.pt}`
      : `Não construa software; reproduza a menor unidade de valor com evidência permitida, sintética ou desidentificada: ${source.wedge.pt}`;
  const commitmentGate = source.verdict === "do_not_build_yet"
    ? `Vá para a decisão final somente se pelo menos ${counts.commitments} de ${counts.offerAudience} pessoas compradoras responsáveis fornecerem a evidência permitida que falta e autoridade de orçamento, ou assumirem compromisso pago; caso contrário, preserve do_not_build_yet.`
    : `Vá para a decisão final somente se pelo menos ${counts.commitments} de ${counts.offerAudience} pessoas compradoras responsáveis assumirem compromisso pago por pagamento recebido, contrato assinado, depósito, ordem de compra ou orçamento aprovado.`;

  return [
    {
      intent: "trigger",
      window: "Dias 1–2",
      goal: `Confirmar o gatilho e a pessoa responsável — ${source.title.pt}`,
      actions: [
        `Monte a lista de potenciais compradores por esta rota documentada: ${source.distribution.pt}`,
        `Entreviste a pessoa compradora responsável definida no dossiê: ${source.targetCustomer.pt}`,
        `Em cada conversa, registre se este gatilho está ativo, tem data e responsável: ${source.purchaseTrigger.pt}`,
      ],
      artifact: `Um livro-razão de gatilhos para ${source.title.pt}, com comprador, evento ativo, prazo, responsável atual, fonte e próxima evidência.`,
      metric: `Conclua ${counts.conversations} conversas com compradores; conte somente fluxo e gatilho atuais observáveis, nunca reuniões marcadas ou interesse declarado.`,
      gate: `Continue somente se pelo menos ${counts.repeatedTriggers} de ${counts.conversations} pessoas compradoras responsáveis documentarem o mesmo gatilho em um fluxo atual e uma decisão, exceção ou prazo real.`,
    },
    {
      intent: "evidence",
      window: "Dias 3–5",
      goal: "Tentar invalidar o problema no trabalho atual",
      actions: [
        `Rastreie ${counts.cases} casos atuais permitidos contra este problema: ${source.problem.pt}`,
        "Em cada caso, registre o improviso atual, responsável pela decisão, exceção, resultado observado e uma evidência contrária.",
        `Teste a hipótese de maior risco antes da entrega: ${firstRisk}`,
      ],
      artifact: `Um mapa do estado atual de ${source.title.pt} com ${counts.cases} casos ligados às fontes, improviso, responsável, exceção, contraevidência e resultado registrado.`,
      metric: `Revise ${counts.cases} casos atuais e preserve pelo menos 1 contraexemplo, sem filtrar a evidência em favor de uma conclusão positiva.`,
      gate: `Avance somente se pelo menos ${counts.completedCases} de ${counts.cases} casos mostrarem a lacuna de decisão recorrente e uma pessoa compradora responsável confirmar o fluxo registrado; caso contrário, pare ou mantenha o veredito atual.`,
    },
    {
      intent: "delivery",
      window: "Dias 6–8",
      goal: deliveryGoal,
      actions: [
        deliveryAction,
        `Mantenha a entrega dentro deste limite de oferta: ${source.initialOffer.pt}`,
        `Registre cada entrada, transição, exceção e decisão ao executar este loop: ${source.coreLoop.pt}`,
      ],
      artifact: `Um pacote de entrega limitado de ${source.title.pt}, com entradas, fontes, exceções, revisão humana e decisão de aprovar, rejeitar ou adiar.`,
      metric: `Execute ${counts.cases} casos limitados e registre a decisão controlada pelo comprador em cada um, incluindo resultados negativos e inconclusivos.`,
      gate: `Continue somente se pelo menos ${counts.completedCases} de ${counts.cases} casos chegarem a uma decisão registrada de aprovar, rejeitar, adiar ou migrar, e a pessoa compradora responsável concluir a transição.`,
    },
    {
      intent: "commitment",
      window: "Dias 9–11",
      goal: source.verdict === "do_not_build_yet" ? "Testar se a evidência conquista compromisso" : "Pedir uma continuação financiada",
      actions: [
        `Apresente uma continuação de escopo fechado somente após a barreira de entrega; use esta hipótese comercial: ${source.businessModel.pt}`,
        `Volte à pessoa compradora responsável pela rota documentada: ${source.distribution.pt}`,
        "Registre preço, escopo, objeção, decisão de contrato ou orçamento e se a pessoa controla o próximo gatilho.",
      ],
      artifact: `Uma oferta de escopo fechado e um livro-razão de objeções de ${source.title.pt}, ligados à evidência entregue e à decisão de orçamento de cada comprador.`,
      metric: `Apresente a oferta a ${counts.offerAudience} pessoas compradoras responsáveis; conte somente pagamento recebido, contrato assinado, depósito, ordem de compra, orçamento aprovado ou rejeição explícita.`,
      gate: commitmentGate,
    },
    {
      intent: "decision",
      window: "Dias 12–14",
      goal: "Aplicar a regra de avançar, adaptar ou parar",
      actions: [
        `Compare a evidência observada com o menor teste do dossiê, sem trocar por métricas mais fáceis: ${source.smallestTest.pt}`,
        `Aplique este critério de descarte sem reinterpretá-lo: ${source.killCriterion.pt}`,
        "Publique um memorando de decisão que marque cada limite como atingido, perdido ou desconhecido e nomeie responsável e próximo prazo.",
      ],
      artifact: `Um memorando assinado de avançar, adaptar ou parar para ${source.title.pt}, com evidências, riscos abertos, resultado do menor teste, regra de descarte, responsável e próxima data.`,
      metric: "Produza 1 decisão explícita até o dia 14; todo limite deve estar marcado como atingido, perdido ou desconhecido.",
      gate: `Avance somente quando o menor teste for cumprido e a regra de descarte não for acionada. Menor teste: ${source.smallestTest.pt} Regra de descarte: ${source.killCriterion.pt}`,
    },
  ];
}

export function buildValidationPlaybook(source: ValidationPlaybookSource): LocalizedValidationPlaybook {
  return {
    en: EnglishPlaybook(source),
    pt: PortuguesePlaybook(source),
  };
}

export function isValidationPlaybook(value: unknown): value is ValidationPlaybookStep[] {
  return Array.isArray(value)
    && value.length === VALIDATION_INTENTS.length
    && value.every((candidate, index) => {
      if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) return false;
      const step = candidate as Record<string, unknown>;
      return step.intent === VALIDATION_INTENTS[index]
        && ["window", "goal", "artifact", "metric", "gate"].every((field) =>
          typeof step[field] === "string" && Boolean((step[field] as string).trim()))
        && Array.isArray(step.actions)
        && step.actions.length >= 2
        && step.actions.every((action) => typeof action === "string" && Boolean(action.trim()));
    });
}
